import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { register, login, getMe, updateMe, logout, forgotPassword, resetPassword, deleteAccount } from '../api/api';
import type { RegisterRequest, LoginRequest, UpdateProfileRequest } from '../api/types';
import useToken from '@/shared/hooks/useToken';
import { getErrorMessage, getErrorResponse } from '@/shared/api/error';
import { AxiosError } from 'axios';
import { hardRedirect } from '@/shared/lib/navigation';

// Query keys
export const authKeys = {
    all: ['auth'] as const,
    me: () => [...authKeys.all, 'me'] as const,
};

/**
 * Hook to register a new user
 */
export const useRegister = () => {
    const { setToken } = useToken();

    return useMutation({
        mutationFn: (data: RegisterRequest) => register(data),
        onSuccess: (response) => {
            if (response.status === 201 && response.data?.success) {
                const accessToken = response.data.data?.access_token;
                if (accessToken) {
                    setToken(accessToken);
                    toast.success('Account created successfully!');
                    // Full page reload to ensure all state is cleared and app reinitializes
                    hardRedirect('/');
                }
            }
        },
        onError: (error: unknown) => {
            const errorResponse = getErrorResponse(error);
            if (errorResponse && typeof errorResponse === 'object' && 'errors' in errorResponse) {
                const errors = errorResponse.errors as Record<string, unknown>;
                // Find first error message
                let firstErrorMessage: string | null = null;
                for (const key in errors) {
                    const errorValue = errors[key];
                    if (Array.isArray(errorValue) && errorValue.length > 0) {
                        const firstError = errorValue[0];
                        if (typeof firstError === 'string') {
                            firstErrorMessage = firstError;
                            break;
                        }
                    }
                }
                // Show only the first error message
                toast.error(firstErrorMessage || getErrorMessage(error));
            } else {
                toast.error(getErrorMessage(error));
            }
        },
    });
};

/**
 * Hook to login user
 */
export const useLogin = () => {
    const { setToken } = useToken();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: LoginRequest) => login(data),
        onSuccess: (response) => {
            if (response.status === 200 && response.data?.success) {
                const accessToken = response.data.data?.access_token;
                if (accessToken) {
                    setToken(accessToken);
                    queryClient.invalidateQueries({ queryKey: authKeys.me() });
                    toast.success('Login successful!');
                    // Full page reload to ensure all state is cleared and app reinitializes
                    hardRedirect('/');
                }
            }
        },
        onError: (error: unknown) => {
            const errorResponse = getErrorResponse(error);
            if (errorResponse && typeof errorResponse === 'object' && 'errors' in errorResponse) {
                const errors = errorResponse.errors as Record<string, unknown>;
                const emailError = errors.email;
                if (emailError) {
                    if (Array.isArray(emailError) && emailError.length > 0 && typeof emailError[0] === 'string') {
                        toast.error(emailError[0]);
                    } else if (typeof emailError === 'string') {
                        toast.error(emailError);
                    }
                } else {
                    toast.error(getErrorMessage(error));
                }
            } else {
                toast.error(getErrorMessage(error));
            }
        },
    });
};

/**
 * Hook to get authenticated user profile
 */
export const useMe = () => {
    const { token } = useToken();

    return useQuery({
        queryKey: authKeys.me(),
        queryFn: () => getMe(),
        enabled: !!token,
        retry: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * Hook to update user profile
 */
export const useUpdateProfile = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: UpdateProfileRequest) => updateMe(data),
        onSuccess: (response) => {
            if (response.status === 200 && response.data?.success) {
                queryClient.invalidateQueries({ queryKey: authKeys.me() });
                toast.success('Profile updated successfully!');
            }
        },
        onError: (error: unknown) => {
            const errorResponse = getErrorResponse(error);
            if (errorResponse && typeof errorResponse === 'object' && 'errors' in errorResponse) {
                const errors = errorResponse.errors as Record<string, unknown>;
                // Find first error message
                let firstErrorMessage: string | null = null;
                for (const key in errors) {
                    const errorValue = errors[key];
                    if (Array.isArray(errorValue) && errorValue.length > 0) {
                        const firstError = errorValue[0];
                        if (typeof firstError === 'string') {
                            firstErrorMessage = firstError;
                            break;
                        }
                    }
                }
                // Show only the first error message
                toast.error(firstErrorMessage || getErrorMessage(error));
            } else {
                toast.error(getErrorMessage(error));
            }
        },
    });
};

/**
 * Hook to logout user
 */
export const useLogout = () => {
    const { setToken } = useToken();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => logout(),
        onSuccess: () => {
            setToken(null);
            queryClient.clear();
            toast.success('Logged out successfully');
            // Full page reload to ensure all state is cleared
            hardRedirect('/');
        },
        onError: (_error: unknown) => {
            setToken(null);
            queryClient.clear();
            // Full page reload to ensure all state is cleared
            hardRedirect('/');
        },
    });
};

/**
 * Hook to request password reset email
 */
export const useForgotPassword = () => {
    return useMutation({
        mutationFn: (data: { email: string }) => forgotPassword(data),
        onSuccess: (response) => {
            if (response.status === 200) {
                toast.success('An email containing a password change link has been sent to you!');
            }
        },
        onError: (error: unknown) => {
            if (error instanceof AxiosError) {
                const status = error.response?.status;
                if (status === 404) {
                    toast.error('User does not exist.');
                } else if (status && status >= 500) {
                    toast.error('Server Error!');
                } else {
                    toast.error('This email does not exist!');
                }
            } else {
                toast.error(getErrorMessage(error));
            }
        },
    });
};

/**
 * Hook to complete password reset
 */
export const useResetPassword = () => {
    const navigate = useNavigate();

    return useMutation({
        mutationFn: (data: { token: string; password: string }) => resetPassword(data),
        onSuccess: (response) => {
            if (response.status === 200) {
                if (response.data?.status === 'OK') {
                    toast.success('Your password has been changed!');
                    setTimeout(() => {
                        navigate('/sign-in');
                    }, 2000);
                } else {
                    toast.error('Your link is expired. You are redirecting to recover password page.');
                    setTimeout(() => {
                        navigate('/forgot-password/');
                    }, 2000);
                }
            }
        },
        onError: (error: unknown) => {
            if (error instanceof AxiosError) {
                const status = error.response?.status;
                if (status === 400) {
                    const errorResponse = getErrorResponse(error);
                    if (errorResponse && typeof errorResponse === 'object' && 'password' in errorResponse) {
                        const passwordError = errorResponse.password;
                        if (Array.isArray(passwordError) && passwordError.length > 0 && typeof passwordError[0] === 'string') {
                            toast.error(passwordError[0]);
                        } else {
                            toast.error('Password is invalid.');
                        }
                    } else {
                        toast.error('Password is invalid.');
                    }
                } else if (status && status >= 500) {
                    toast.error('Server Error!');
                } else {
                    toast.error('Failed to reset password. Please try again.');
                }
            } else {
                toast.error(getErrorMessage(error));
            }
        },
    });
};

/**
 * Hook to delete user account
 */
export const useDeleteAccount = () => {
    const { setToken } = useToken();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => deleteAccount(),
        onSuccess: (response) => {
            if (response.status === 200 && response.data?.success) {
                setToken(null);
                queryClient.clear();
                toast.success('Account deleted successfully');
                // Full page reload to ensure all state is cleared
                hardRedirect('/sign-in');
            }
        },
        onError: (error: unknown) => {
            toast.error(getErrorMessage(error));
        },
    });
};
